import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { ArrowUp } from 'lucide-react'
import { gsap, MOTION, ScrollTrigger, useGSAP } from '@/lib/motion'
import { scrollToTarget } from '@/lib/smooth-scroll'
import { LocalTime } from '@/components/generals/local-time'
import { SectionLink } from '@/components/generals/section-link'
import {
    COPYRIGHT_YEAR,
    NAV_ITEMS,
    QUOTE_HASH,
    siteConfig
} from '@/constants/site'

const WORDMARK = 'Localboost'

interface SiteFooterProps {
    inert?: boolean
}

export const SiteFooter: FC<SiteFooterProps> = ({ inert }): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const markRef = useRef<HTMLSpanElement>(null)
    const [year, setYear] = useState(COPYRIGHT_YEAR)

    useEffect(() => {
        setYear(new Date().getFullYear())
    }, [])

    useEffect(() => {
        const mark = markRef.current
        const box = mark?.parentElement

        if (!mark || !box) return

        let lastWidth = 0

        const fit = (force = false) => {
            const width = box.clientWidth

            if (!force && Math.abs(width - lastWidth) < 1) return

            lastWidth = width
            mark.style.fontSize = '100px'

            const natural = mark.scrollWidth

            if (natural > 0) mark.style.fontSize = `${(width / natural) * 100}px`

            ScrollTrigger.refresh()
        }

        const observer = new ResizeObserver(() => fit())

        observer.observe(box)
        document.fonts?.ready.then(() => fit(true)).catch(() => undefined)

        return () => observer.disconnect()
    }, [])

    useGSAP(() => {
        const footer = ref.current

        if (!footer) return

        const chars = footer.querySelectorAll('[data-char]')
        const mark = footer.querySelector('[data-wordmark]')
        const mm = gsap.matchMedia()

        mm.add(MOTION.ok, () => {
            gsap.fromTo(chars, { yPercent: 105 }, {
                yPercent: 0,
                ease: 'none',
                stagger: 0.06,
                scrollTrigger: {
                    trigger: mark,
                    start: 'top bottom',
                    end: 'bottom bottom',
                    scrub: 0.6
                }
            })
        })

        return () => mm.revert()
    }, { scope: ref })

    return (
        <footer
            ref={ref}
            data-tone='ink'
            data-surface='dark'
            inert={inert}
            className='relative z-10 overflow-clip bg-ink text-paper'
        >
            <div className='px-(--gutter) pt-24 md:pt-36'>
                <div className='grid gap-x-8 gap-y-14 md:grid-cols-12'>
                    <div className='md:col-span-5'>
                        <p className='label text-haze'>Localboost, web design &amp; marketing</p>

                        <p className='mt-6 max-w-[22ch] text-[clamp(1.6rem,2.6vw,2.4rem)] font-medium leading-[1.12] tracking-[-0.02em] wdth-87'>
                            We&apos;re in the results business. Let Localboost help your business grow.
                        </p>
                    </div>

                    <nav aria-label="Footer" className='md:col-span-3 md:col-start-7'>
                        <p className='label text-haze'>Explore</p>

                        <ul className='mt-5 space-y-2 text-lg'>
                            {NAV_ITEMS.map(item => (
                                <li key={item.href}>
                                    <SectionLink hash={item.href} className='transition-colors hover:text-signal'>
                                        {item.label}
                                    </SectionLink>
                                </li>
                            ))}

                            <li>
                                <Link href='/work' className='transition-colors hover:text-signal'>
                                    All 50 design examples
                                </Link>
                            </li>

                            <li>
                                <SectionLink hash={QUOTE_HASH} className='text-signal transition-colors hover:text-paper'>
                                    Get a free quote
                                </SectionLink>
                            </li>
                        </ul>
                    </nav>

                    <div className='md:col-span-3 md:col-start-10'>
                        <p className='label text-haze'>Studio</p>

                        <address className='mt-5 space-y-1 text-lg not-italic'>
                            <span className='block'>{siteConfig.legalName}</span>
                            <span className='block text-haze'>{siteConfig.location}</span>
                        </address>

                        <p className='label mt-6 flex items-center gap-2.5 text-haze'>
                            <span aria-hidden='true' className='size-1.5 rounded-full bg-signal' />
                            Local time
                            <LocalTime className='text-paper' />
                        </p>
                    </div>
                </div>

                <div data-wordmark='' aria-hidden='true' className='mt-20 select-none md:mt-28'>
                    <span
                        ref={markRef}
                        className='display block w-max whitespace-nowrap text-[15vw] leading-[0.78] tracking-[-0.055em] wdth-125'
                    >
                        {WORDMARK.split('').map((char, index) => (
                            <span key={`${char}-${index}`} className='line-mask'>
                                <span data-char=''>{char}</span>
                            </span>
                        ))}
                    </span>
                </div>

                <div className='hairline mt-8 flex flex-col gap-5 border-t py-7 md:flex-row md:items-center md:justify-between'>
                    <p className='label text-haze'>
                        &copy; {year} {siteConfig.legalName}. All rights reserved.
                    </p>

                    <div className='flex items-center gap-8'>
                        <Link href='/terms-of-service' className='label transition-colors hover:text-signal'>
                            Terms of Service
                        </Link>

                        <button
                            type='button'
                            onClick={() => scrollToTarget(0)}
                            className='label inline-flex items-center gap-2 transition-colors hover:text-signal'
                        >
                            Back to top
                            <ArrowUp aria-hidden='true' className='size-3.5' />
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    )
}
